/**
 * このアプリが PDS に書き込む / 読み出す NSID の一元管理。
 *
 * 環境変数:
 *   - VITE_NSID_ROOT (必須): プロジェクト固有の NSID prefix。
 *       例: 本家 = "app.aozoraquest", fork = "com.example.myapp"
 *   - VITE_NSID_ENV (任意): 同一プロジェクト内で env を分離する suffix。
 *       例: dev = "dev" → user 記録は "{ROOT}.dev.{name}" に書く。
 *       未指定 = production 扱いで "{ROOT}.{name}" に書く。
 *
 * Cloudflare Workers Builds の build env vars に必ず設定する。
 * 未指定だと build を失敗させる (silent に誤デプロイするのを防ぐ)。
 *
 * 区分:
 *   - user 系 (per-user PDS, env で分離する) → COL
 *       analysis / profile / spiritChat / questLog / cardDraw
 *   - admin 系 (主管理者の PDS) → ADMIN_COL
 *       world.* / config.* は env で分離する (#716): VITE_NSID_ENV が空でなければ
 *       "{ROOT}.dev.*" (ローカルも dev エッジを叩くので dev と同じ)、未指定 = 本番 "{ROOT}.*"。
 *       directory (毎時の自動更新が本番 Environment で書く) と questIndex (rkey で分離) は共有。
 *
 * 注意:
 * - 実 Bluesky 投稿 (`app.bsky.feed.post`) は性質上分離不可、
 *   dev からの投稿も実投稿になる。
 */
import { ADMIN_WORLD_RECORDS, adminNsidPrefix, adminWorldCollection, type AdminWorldRecordName } from '@aozoraquest/core';

const ROOT = (import.meta.env.VITE_NSID_ROOT as string | undefined)?.trim();
if (!ROOT) {
  throw new Error(
    'VITE_NSID_ROOT is required (e.g. "app.aozoraquest"). ' +
      'Set it in the Cloudflare Workers Builds env vars per project.',
  );
}
const ENV = (import.meta.env.VITE_NSID_ENV as string | undefined)?.trim();
const USER_PREFIX = ENV ? `${ROOT}.${ENV}` : ROOT;
/** 管理データ (world.* / config.*) の prefix。規則は core の adminNsidPrefix (edge と共通。#716)。 */
const ADMIN_PREFIX = adminNsidPrefix(ROOT, ENV);

export const COL = {
  analysis: `${USER_PREFIX}.analysis`,
  profile: `${USER_PREFIX}.profile`,
  spiritChat: `${USER_PREFIX}.spiritChat`,
  questLog: `${USER_PREFIX}.questLog`,
  cardDraw: `${USER_PREFIX}.cardDraw`,
  /** あおぞらパワーの累積カウンタ (rkey='self')。アクションごとに増分する。
   *  初回読み取り時に旧データから一度だけスキャン → 書き込みでマイグレーション。 */
  power: `${USER_PREFIX}.power`,
  /** 戦闘記録 (1 戦 1 レコード)。旧ブルスコンの試練が書いていた履歴。試練撤去後は
   *  新規書き込み経路は無いが、読み取りは生きている: world の在庫フォールバック
   *  (loadBattleStats の materials) と パワー再スキャン (countBattles)。
   *  wins/連勝/称号系フィールドは現状どの UI からも未使用 (整理は #383)。 */
  battle: `${USER_PREFIX}.battle`,
  /** なんでも屋の制作 (docs/20)。1 制作 = 1 レコード。品質は rkey + luk から導出 */
  craft: `${USER_PREFIX}.craft`,
  /** 装備中 (docs/20 W6c)。rkey='self'。craft/forge の rkey 参照 (強化値は直書きしない) */
  gear: `${USER_PREFIX}.gear`,
  /** あおぞらワールドの状態 (docs/19-overworld.md)。rkey='self'。位置・乗り物・
   *  解禁リージョン。PR-W3 以降は Worker (DO) が正で、これは表示キャッシュ + 監査線。 */
  world: `${USER_PREFIX}.world`,
  /** 依頼クエスト (docs/15-user-quest.md)。tid key。発注者の PDS に書く。 */
  userQuest: `${USER_PREFIX}.userQuest`,
  /** 応募 (docs/15-user-quest.md)。tid key。応募者の PDS に書く。 */
  questApplication: `${USER_PREFIX}.questApplication`,
  /** 完了報告 / 承認 / やり直し依頼 (docs/15-user-quest.md)。tid key。 */
  questCompletion: `${USER_PREFIX}.questCompletion`,
} as const;

/**
 * 他ユーザーのレコードを読みに行くときの NSID (ROOT 直下 = production view)。
 *
 * 自分の WRITE は env-isolated な COL.* に行くが、他ユーザーは production
 * NSID にしか書いていない。dev 環境の resonance タイムラインや friends 画面で
 * 他者の archetype を表示するためには ROOT_COL から読む必要がある。
 *
 * production 環境 (VITE_NSID_ENV 未指定) では COL と ROOT_COL は同値になる。
 */
export const ROOT_COL = {
  analysis: `${ROOT}.analysis`,
  profile: `${ROOT}.profile`,
} as const;

export const ADMIN_COL = {
  configFlags: `${ADMIN_PREFIX}.config.flags`,
  configMaintenance: `${ADMIN_PREFIX}.config.maintenance`,
  configBans: `${ADMIN_PREFIX}.config.bans`,
  configPrompts: `${ADMIN_PREFIX}.config.prompts`,
  directory: `${ROOT}.directory`,
  /** world.* (地形 #421・絵 #421・モンスター #419・アイテム #420・店 #422・NPC #425・
   *  クエスト #423・ジョブ #544・内部マップ #424・シナリオ #545)。名前は core の ADMIN_WORLD_RECORDS。 */
  worldMap: adminWorldCollection(ADMIN_PREFIX, 'map'),
  tileArt: adminWorldCollection(ADMIN_PREFIX, 'tileArt'),
  monsters: adminWorldCollection(ADMIN_PREFIX, 'monsters'),
  items: adminWorldCollection(ADMIN_PREFIX, 'items'),
  shops: adminWorldCollection(ADMIN_PREFIX, 'shops'),
  npcs: adminWorldCollection(ADMIN_PREFIX, 'npcs'),
  quests: adminWorldCollection(ADMIN_PREFIX, 'quests'),
  jobs: adminWorldCollection(ADMIN_PREFIX, 'jobs'),
  interiors: adminWorldCollection(ADMIN_PREFIX, 'interiors'),
  scenario: adminWorldCollection(ADMIN_PREFIX, 'scenario'),
  /** 依頼クエスト集約 (docs/15-user-quest.md §集約インフラ)。
   *  Worker が主管理者 PDS に書く。env 別に rkey を分ける (dev→'dev', prod→'self')。 */
  questIndex: `${ROOT}.questIndex`,
} as const;

/** world.* のレコード名 → collection (共通ローダー loadAdminWorld 用)。 */
export const ADMIN_WORLD_COL = Object.fromEntries(
  ADMIN_WORLD_RECORDS.map((name) => [name, adminWorldCollection(ADMIN_PREFIX, name)]),
) as Record<AdminWorldRecordName, string>;

/** ADMIN_COL.questIndex の rkey: env 別に分離する */
export const QUEST_INDEX_RKEY = ENV ? ENV : 'self';
